import { BodySmallBold, Button, H2, Spinner } from '@clubspark-react/clubspark-react-tools'
import { Grid, Hidden } from '@material-ui/core'
import { navigate, RouteComponentProps } from '@reach/router'
import { Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MembershipCodeEnum } from 'src/graphql-types/globalTypes'
import { usePrice } from 'src/hooks/use-price'
import { USTASectionInfo } from 'src/types/types'
import { HOME_ROUTE, USTA_SECTION_PAYMENT_ROUTE } from 'src/utils/constants/routes'
import {
  getTermId,
  getUSTASectionInfo,
  setCurrentStep,
  setUSTAInfo
} from 'src/utils/localStorage/local-storage'
import * as yup from 'yup'
import AddressForm, { Input } from '../address-form/address-form'
import FormErrorMessage from '../form-error-message/form-error-message'
import MembershipSummary from '../membership-summary/membership-summary'
import styles from './usta-section-details.module.scss'
import { useMembershipTerms } from '../../auth/register/register'

interface USTASectionDetailsDetailsProps extends RouteComponentProps {}

const USTASectionDetails: React.FC<USTASectionDetailsDetailsProps> = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const { hasMultipleActiveTerms } = useMembershipTerms(MembershipCodeEnum.USTASEC)
  const { amount } = usePrice({
    skip: !getTermId(),
    variables: { input: { membershipCode: MembershipCodeEnum.USTASEC, termId: getTermId() } }
  })

  const ustaSectionInfo = getUSTASectionInfo()

  let initialValues: USTASectionInfo = {
    ustaSectionName: ustaSectionInfo ? ustaSectionInfo.ustaSectionName : '',
    city: ustaSectionInfo ? ustaSectionInfo.city : '',
    mailingAddress: ustaSectionInfo ? ustaSectionInfo.mailingAddress : '',
    state: ustaSectionInfo ? ustaSectionInfo.state : '',
    zipCode: ustaSectionInfo ? ustaSectionInfo.zipCode : ''
  }

  const validationSchema = yup.object().shape({
    ustaSectionName: yup.string().required(t('is required', { type: 'usta section name' })),
    mailingAddress: yup.string().required(t('is required', { type: 'mailing address' })),
    city: yup.string().required(t('is required', { type: 'city' })),
    state: yup.string().required(t('is required', { type: 'state' })),
    zipCode: yup.string().required(t('is required', { type: 'zip code' }))
  })

  const handleBack = () => {
    navigate(HOME_ROUTE)
  }

  const onSubmit = async (values: USTASectionInfo, actions) => {
    setLoading(true)
    actions.setSubmitting(true)
    setUSTAInfo(values)
    setLoading(false)
    actions.setSubmitting(false)
    if (!loading) {
      navigate(USTA_SECTION_PAYMENT_ROUTE)
      setCurrentStep(hasMultipleActiveTerms ? '2' : '1')
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => onSubmit(values, actions)}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ errors, touched, isSubmitting, values, setFieldValue }) => (
        <Form>
          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={8} lg={6}>
              <Grid container>
                <Grid item xs={12} md={12} lg={12}>
                  <H2 spacing={{ margins: { xxs: 'bottom' } }}>{t('usta section details')}</H2>
                </Grid>
                <Grid container>
                  <BodySmallBold spacing={{ margins: { md: 'top', xxs: 'bottom' } }}>
                    {t('usta section name')} *
                  </BodySmallBold>
                  <Field name="ustaSectionName" component={Input} />
                  {errors?.ustaSectionName && touched?.ustaSectionName && (
                    <FormErrorMessage message={errors.ustaSectionName} />
                  )}
                </Grid>
                <AddressForm
                  errors={errors}
                  isSubmitting={isSubmitting}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  values={values}
                />
                {isSubmitting ? (
                  <Grid container justify="flex-start" className={styles.spinnerContainer}>
                    <Spinner />
                  </Grid>
                ) : (
                  <Grid
                    container
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    className={styles.actionButtonContainer}
                  >
                    <div className={styles.root}>
                      <Button
                        type="button"
                        classname={styles.buttonBack}
                        size="lg"
                        level="secondary"
                        onClick={handleBack}
                      >
                        {t('back')}
                      </Button>
                      <Button type="submit" size="lg" classname={styles.button}>
                        {t('continue')}
                      </Button>
                    </div>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Hidden xsDown>
              <MembershipSummary cost={amount} />
            </Hidden>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
export default USTASectionDetails
